export function createQualityEntryProjectionService(deps) {
    const {
        selectedQualityContextRef,
        fukushizaiDetailSyncingRef,
        PANEL_TYPE,
        QUALITY_ROOT_DATA_PREFIXES,
        isEmpty,
        DicVal,
        normalizeColumnIdUpper,
        normalizeFukushizaiContext,
        getDetailRowsByPanelType,
        rowMatchesFukushizaiItemGroup,
        rowMatchesFukushizaiItemGroupColor,
        getQualityBaseFieldIds,
        readFukushizaiItemGroupFromAnyRow,
        setQualityEntryProjectionContext,
        activateFukushizaiProjectionGuard
    } = deps;

    // entry 上に一時展開された品質表示 BASE 項目をクリアします。
    function clearQualityBaseMergedFields(entryObj) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return;

        Object.keys(entryObj).forEach((key) => {
            const cid = normalizeColumnIdUpper(key);
            if (QUALITY_ROOT_DATA_PREFIXES.some((prefix) => cid.startsWith(prefix))) delete entryObj[key];
        });

        getQualityBaseFieldIds().forEach((fieldId) => {
            delete entryObj[fieldId];
            delete entryObj[`${fieldId}_nm`];
            delete entryObj[`${fieldId}_NM`];
            delete entryObj[`${fieldId}_cd`];
            delete entryObj[`${fieldId}_CD`];
        });

        delete entryObj.__QUALITY_PROJECTED_ITEMCD;
        delete entryObj.__QUALITY_PROJECTED_GROUPNO;
        delete entryObj.__QUALITY_PROJECTED_COLORCD;
    }

    // 現在の item/group/color に対する品質表示代表行を選びます。
    function pickQualityRepresentativeRow(detailData, contextOverride = null) {
        // 品質表示は副資材と独立した選択のため、既定は品質側 context を使う。
        const context = normalizeFukushizaiContext(contextOverride || selectedQualityContextRef.current);
        if (context.itemCd === "" || context.groupNo === "") {
            return { row: null, index: -1, panelType: PANEL_TYPE.QUALITY_BASE };
        }

        const panelTypes = [PANEL_TYPE.QUALITY_BASE, "119"];
        for (let panelIndex = 0; panelIndex < panelTypes.length; panelIndex++) {
            const panelType = panelTypes[panelIndex];
            const rows = getDetailRowsByPanelType(detailData, panelType);
            const list = Array.isArray(rows) ? rows : [];

            let index = list.findIndex((row) =>
                rowMatchesFukushizaiItemGroupColor(row, context.itemCd, context.groupNo, context.colorCd)
            );
            if (index < 0) {
                index = list.findIndex((row) =>
                    rowMatchesFukushizaiItemGroup(row, context.itemCd, context.groupNo)
                );
            }
            if (index >= 0) return { row: list[index], index, panelType };
        }

        return { row: null, index: -1, panelType: PANEL_TYPE.QUALITY_BASE };
    }

    // 摘要(SSDE_TEKIYO)の実体は洗濯表示(D_SSDE)。同じアイテム+グループの洗濯行から単票へ投影します。
    // (SSDQ_プレフィックスでないため品質BASE項目の投影には乗らない)
    const QUALITY_TEKIYO_FIELD = "SSDE_TEKIYO";
    function mergeQualityTekiyoToEntry(entryObj, detailData, context) {
        if (context.itemCd === "" || context.groupNo === "") return;
        const rows = getDetailRowsByPanelType(detailData, PANEL_TYPE.WASH);
        const hit = (Array.isArray(rows) ? rows : []).find((row) =>
            !isEmpty(row)
            && rowMatchesFukushizaiItemGroup(row, context.itemCd, context.groupNo)
            && String(DicVal(row, QUALITY_TEKIYO_FIELD, "") ?? "").trim() !== "");
        entryObj[QUALITY_TEKIYO_FIELD] = isEmpty(hit) ? "" : hit[QUALITY_TEKIYO_FIELD];
    }

    // 代表行の品質表示 BASE 値を entry へ投影します。
    function mergeQualityRepresentativeRowToEntry(entryObj, detailData, contextOverride = null) {
        if (isEmpty(entryObj) || isEmpty(detailData)) return false;

        const context = normalizeFukushizaiContext(contextOverride || selectedQualityContextRef.current);
        const picked = pickQualityRepresentativeRow(detailData, context);
        const row = DicVal(picked, "row", null);
        const previousSyncing = fukushizaiDetailSyncingRef.current;
        fukushizaiDetailSyncingRef.current = true;
        activateFukushizaiProjectionGuard();

        try {
            clearQualityBaseMergedFields(entryObj);
            mergeQualityTekiyoToEntry(entryObj, detailData, context);

            // 代表行が無い場合でも、現在 context は entry 側へ残します。
            if (isEmpty(row)) {
                if (context.itemCd !== "" && context.groupNo !== "") {
                    setQualityEntryProjectionContext(entryObj, context);
                }
                return false;
            }

            getQualityBaseFieldIds().forEach((fieldId) => {
                if (Object.prototype.hasOwnProperty.call(row, fieldId)) entryObj[fieldId] = row[fieldId];
                [`${fieldId}_cd`, `${fieldId}_CD`, `${fieldId}_nm`, `${fieldId}_NM`].forEach((displayFieldId) => {
                    if (Object.prototype.hasOwnProperty.call(row, displayFieldId)) entryObj[displayFieldId] = row[displayFieldId];
                });
            });

            setQualityEntryProjectionContext(entryObj, readFukushizaiItemGroupFromAnyRow(row));
            return true;
        } finally {
            fukushizaiDetailSyncingRef.current = previousSyncing;
        }
    }

    return {
        clearQualityBaseMergedFields,
        pickQualityRepresentativeRow,
        mergeQualityRepresentativeRowToEntry
    };
}
